import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import { storageService } from '@/services/local-storage';
import type { Evaluation } from '@/types/evaluation.types';

/**
 * Hook to fetch all evaluations from storage
 *
 * @returns Query result with evaluations array
 *
 * @example
 * ```tsx
 * function EvaluationList() {
 *   const { data: evaluations, isLoading, error } = useEvaluations();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *
 *   return (
 *     <div>
 *       {evaluations.map(eval => (
 *         <EvaluationCard key={eval.id} evaluation={eval} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useEvaluations(): UseQueryResult<Evaluation[], Error> {
  return useQuery({
    queryKey: queryKeys.evaluations.all,
    queryFn: () => {
      const evaluations = storageService.getEvaluations();
      return evaluations;
    },
  });
}

/**
 * Hook to fetch a single evaluation by ID
 *
 * @param id - Evaluation ID to fetch
 * @returns Query result with evaluation or null if not found
 *
 * @example
 * ```tsx
 * function EvaluationDetail({ id }: { id: string }) {
 *   const { data: evaluation, isLoading, error } = useEvaluation(id);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *   if (!evaluation) return <NotFound />;
 *
 *   return <EvaluationDetails evaluation={evaluation} />;
 * }
 * ```
 */
export function useEvaluation(id: string): UseQueryResult<Evaluation | null, Error> {
  return useQuery({
    queryKey: queryKeys.evaluations.detail(id),
    queryFn: () => {
      const evaluation = storageService.getEvaluation(id);
      return evaluation;
    },
    enabled: !!id, // Only run query if id is provided
  });
}
